// Панель «Структура» на вкладке «Сотрудники» (037: FR-001, FR-002, FR-008; research, Р1, Р2;
// ORG-01, ORG-02, ORG-05). Узлы — организации и подразделения, которые пользователь видит; числа —
// видимые ему сотрудники по основному месту работы с вложенными. Узлы не хранятся: сервер считает
// их по снимку справочника теми же правилами видимости, что список.
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { OrganizationSideSchema } from './record.ts';

const count = z.number().int().min(0);

export const staffNodeKinds = ['organization', 'unit'] as const;
export type StaffNodeKind = (typeof staffNodeKinds)[number];
export const StaffNodeKindSchema = z.enum(staffNodeKinds);

export const StaffNodeSchema = z.object({
  kind: StaffNodeKindSchema,
  id: IdSchema,
  /** Организация узла; у организации — она сама. */
  organizationId: IdSchema,
  /** У подразделения — родитель или null (корень организации); у организации — null. */
  parentId: IdSchema.nullable(),
  name: z.string(),
  /** Сторона организации узла. */
  side: OrganizationSideSchema,
  /** Организация в архиве, где ещё работают видимые сотрудники (у подразделения — false). */
  archived: z.boolean(),
  /** Видимые сотрудники узла и вложенных, «черновик» и «подтверждён» (FR-002). */
  count,
});
export type StaffNode = z.infer<typeof StaffNodeSchema>;

export const StaffStructureSchema = z.object({
  /** Порядок дерева: организации по алфавиту, под каждой — её подразделения по алфавиту. */
  nodes: z.array(StaffNodeSchema),
  /** «Все сотрудники». */
  total: count,
  /** «Внешние организации». */
  external: count,
  /** «Без места работы». */
  none: count,
});
export type StaffStructure = z.infer<typeof StaffStructureSchema>;

/** Узел в условиях списка и в адресе страницы (research, Р3). */
export const STAFF_NODE_PATTERN =
  /^(?:external|none|(?:organization|unit):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export type StaffNodeKey = 'external' | 'none' | `organization:${string}` | `unit:${string}`;

/** Узел из строки условия; не узел — null. */
export function parseStaffNode(
  value: string,
): { kind: 'external' | 'none' } | { kind: 'organization' | 'unit'; id: string } | null {
  if (!STAFF_NODE_PATTERN.test(value)) return null;
  const lower = value.toLowerCase();
  if (lower === 'external' || lower === 'none') return { kind: lower };
  const [kind, id] = lower.split(':') as ['organization' | 'unit', string];
  return { kind, id };
}
